import { useLocation } from 'react-router-dom';
import React, { useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Loader2, MessageSquare } from 'lucide-react';
import moment from 'moment';

import { getShowNewLayout } from '@/components/sidebar/layout-preference';
import InfiniteScroller from '@/components/ui/custom/InfiniteScroller';

import ChatBubble from './ChatBubble';

import {
	getTeamConversationsPreviousMessages,
	markConversationAsRead,
} from '../../../actions/teams';

// Messages from one person within five minutes on the same day read as one run
const RUN_MINUTES = 5;

const isSameRun = (a, b) =>
	!!a &&
	!!b &&
	a.type !== 'event' &&
	b.type !== 'event' &&
	a._user === b._user &&
	moment(a.createdAt).isSame(b.createdAt, 'day') &&
	Math.abs(moment(b.createdAt).diff(moment(a.createdAt), 'minutes')) < RUN_MINUTES;

// Where to open the thread for the message the address asks for (?_target):
// a mention (with ?_teamMention, centred) or a search result (at the top).
// The newest message is what the bottom shows anyway. Sets the list's own
// scroll (scrollIntoView would also shift the page).
const getLinkedMessageScrollTop = (list, newestMessageId) => {
	const params = new URLSearchParams(window.location.search);
	const id = params.get('_target');
	const mentionTarget = Boolean(params.get('_teamMention'));
	if (!id || id === newestMessageId) {
		return null;
	}
	const element = Array.from(list.querySelectorAll('[id]')).find((el) => el.id === id);
	if (!element) {
		return null;
	}
	const rect = element.getBoundingClientRect();
	const top = rect.top - list.getBoundingClientRect().top + list.scrollTop;
	return mentionTarget ? top - (list.clientHeight - rect.height) / 2 : top;
};

// Today, Yesterday, the weekday this week, otherwise the date
const formatDayLabel = (date) =>
	moment(date).calendar(null, {
		sameDay: '[Today]',
		lastDay: '[Yesterday]',
		lastWeek: 'dddd',
		sameElse: 'll',
		nextDay: 'll',
		nextWeek: 'll',
	});

const TeamMessages = ({
	assignedTypeSelected,
	infiniteScrollHeight,
	showAvatar = true,
}) => {
	const dispatch = useDispatch();
  const location = useLocation();
  const mentionTarget = new URLSearchParams(location.search).get('_teamMention') ? new URLSearchParams(location.search).get('_target') : null;
	const listRef = useRef(null);
	const isLoadingPreviousMessages = useRef(false);
	const lastMessageIdRef = useRef(null);

	const [hasMore, setHasMore] = React.useState(true);

	const users = useSelector((state) => state.users);
	const auth = useSelector((state) => state.auth);
	const teams = useSelector((state) => state.teams);

	const [isLoading, setIsLoading] = useState(false);

	const activeTeamConversation = teams.teamConversations.find(
		(tc) => tc._id === teams._activeTeamConversation
	);

	const isRunner = activeTeamConversation?.isRunner;

	const _activeTeamConversation =
		activeTeamConversation && activeTeamConversation._id;

	const messages = activeTeamConversation
		? activeTeamConversation.teamMessages
		: [];

	// TODO: lee look into this message spreading
	const sortedMessages = [...messages].sort((a, b) =>
		a.createdAt < b.createdAt ? -1 : 1
	);

	// reset hasMore when selecting new conversation
	useEffect(() => {
		if (listRef.current && _activeTeamConversation) {
			listRef.current.scrollTop = listRef.current.scrollHeight;
			// initialize last seen message id on conversation switch
			const lastMsg = sortedMessages[sortedMessages.length - 1];
			lastMessageIdRef.current = lastMsg ? lastMsg._id : null;
		}
	}, [_activeTeamConversation]);

	useEffect(() => {
		if (isLoadingPreviousMessages.current) {
			return;
		}

		if (!listRef.current || !_activeTeamConversation) {
			return;
		}

		const lastMsg = sortedMessages[sortedMessages.length - 1];
		const lastSeenId = lastMessageIdRef.current;

		if (lastMsg && lastMsg._id !== lastSeenId) {
			const isFromAuthenticatedUser = lastMsg._user === auth.user._id;
			if (isFromAuthenticatedUser) {
				listRef.current.scrollTo({
					top: listRef.current.scrollHeight,
					behavior: 'smooth',
				});
			}
			lastMessageIdRef.current = lastMsg._id;
		}
	}, [_activeTeamConversation, sortedMessages, auth.user._id]);

	// A new message marks the open conversation unread for everyone but the
	// sender, so mark it read again while it is on screen.
	const isUnreadForMe =
		!!activeTeamConversation?._unreadUsers?.includes(auth.user._id);
	const unreadUsersRef = useRef(null);
	unreadUsersRef.current = activeTeamConversation?._unreadUsers;
	// Runs the pending mark-read when a hidden thread comes on screen.
	const markReadWhenShownRef = useRef(null);

	useEffect(() => {
		if (!isUnreadForMe || !_activeTeamConversation) {
			return undefined;
		}

		const markRead = () => {
			// Phones keep the open chat mounted but hidden behind the list, so
			// only a thread that is on screen counts as read.
			if (document.hidden || !listRef.current?.clientHeight) {
				return;
			}

			document.removeEventListener('visibilitychange', markRead);
			markReadWhenShownRef.current = null;
			dispatch(
				markConversationAsRead(
					{
						_id: _activeTeamConversation,
						_unreadUsers: unreadUsersRef.current,
					},
					auth.user._id
				)
			);
		};

		markReadWhenShownRef.current = markRead;
		markRead();
		document.addEventListener('visibilitychange', markRead);

		return () => {
			markReadWhenShownRef.current = null;
			document.removeEventListener('visibilitychange', markRead);
		};
	}, [isUnreadForMe, _activeTeamConversation, auth.user._id, dispatch]);

	// When the thread gets shorter (the phone keyboard opens, the window is
	// resized or the device rotates) keep the newest message in view if it
	// was in view before, instead of leaving it under the composer.
	useEffect(() => {
		const list = listRef.current;
		if (!list || typeof ResizeObserver === 'undefined') {
			return undefined;
		}

		let wasAtBottom = null;
		const measure = () => {
			wasAtBottom = list.scrollHeight - list.scrollTop - list.clientHeight < 40;
		};
		// it opens at the newest message (the effect above)
		measure();
		let lastHeight = list.clientHeight;
		const observer = new ResizeObserver(() => {
			const height = list.clientHeight;
			if (height === lastHeight) return;
			const wasHidden = lastHeight === 0;
			lastHeight = height;
			if (wasHidden) {
				// Shown again (phones open the thread over the list; a hidden
				// list lost its scroll position): open at a search result or
				// mention, otherwise at the newest message.
				const linkedTop = getLinkedMessageScrollTop(list, lastMessageIdRef.current);
				list.scrollTop = linkedTop ?? list.scrollHeight;
				markReadWhenShownRef.current?.();
			} else if (wasAtBottom) {
				list.scrollTop = list.scrollHeight;
			}
			measure();
		});

		list.addEventListener('scroll', measure, { passive: true });
		observer.observe(list);

		return () => {
			list.removeEventListener('scroll', measure);
			observer.disconnect();
		};
	}, [_activeTeamConversation]);

	// Determine the height based on localStorage or use the prop/default
	const getScrollHeight = () => {
		if (infiniteScrollHeight) {
			return infiniteScrollHeight;
		}

		// No auth: the sign-up preview keeps this pane's old height.
		const showNewLayout = getShowNewLayout();

		return showNewLayout ? 'h-full sm:h-[calc(85dvh)]' : 'h-[calc(82vh)]';
	};

	const scrollHeight = getScrollHeight();
	const isNumericHeight = typeof scrollHeight === 'number';

	if (!_activeTeamConversation) {
		return (
			<div className="flex h-full flex-col items-center justify-center rounded-lg border-2 border-dashed bg-gray-50 p-6 text-center mt-0 m-2">
				<MessageSquare className="mx-auto h-6 w-12 text-gray-400" />
				<h3 className="mt-1 text-md font-semibold text-gray-900">
					Error loading messages
				</h3>
				<p className="mt-1 text-sm text-gray-500">
					Please refresh the page and try again.
				</p>
			</div>
		);
	}

	const directMessage = assignedTypeSelected === 'DIRECT_MESSAGES';

	// deleted messages are hidden, so runs and days skip them
	const shownMessages = sortedMessages.filter((m) => !m.isDeleted);

	const loadMessages = async () => {
		if (isLoading || !hasMore || sortedMessages.length === 0) {
			return;
		}

		isLoadingPreviousMessages.current = true;
		setIsLoading(true);

		const previousScrollHeight = listRef.current.scrollHeight;

		// A failed load stops paging so the spinner does not spin forever
		const { hasMore: newHasMore } = await dispatch(
			getTeamConversationsPreviousMessages({
				_teamConversation: activeTeamConversation._id,
				earliestMessageDate: sortedMessages[0].createdAt,
			})
		);

		setHasMore(!!newHasMore);

		// Use a timeout to allow the DOM to update with new messages
		setTimeout(() => {
			if (listRef.current) {
				const newScrollHeight = listRef.current.scrollHeight;
				listRef.current.scrollTop =
					newScrollHeight - previousScrollHeight;
			}
			setIsLoading(false);
			isLoadingPreviousMessages.current = false;
		}, 0);
	};

	const activeUsersInConversation = users.filter((user) => {
		// Check if the user ID exists in activeTeamConversation._users
		const isActiveUser = activeTeamConversation._users.includes(user._id);
		// Check if the user is active
		const isActive = user.active === true;
		// Return true if both conditions are met
		return isActiveUser && isActive;
	});

	// prevents actions on messages from showing up if the user in a
	const checkIfAllUsersAreActiveInCurrentConversation = () => {
		// if it is a direct message and one of the users are not active, return false
		if (
			activeUsersInConversation.length !==
				activeTeamConversation._users.length &&
			!directMessage
		) {
			return true;
		}

		// If all users are active, return true
		return (
			activeUsersInConversation.length ===
			activeTeamConversation._users.length
		);
	};

	if (!activeTeamConversation) {
		return null;
	}

	return (
		<div
			className={`${
				!isNumericHeight ? scrollHeight : ''
			} w-full min-h-[200px] overflow-y-auto px-2 max-xl:min-h-0 max-md:overscroll-contain`}
			style={isNumericHeight ? { height: `${scrollHeight}px` } : {}}
			ref={listRef}
			data-onboarding={isRunner ? undefined : 'teams-messages'}
		>
			<div className="flex w-full flex-col items-center pb-3 pt-1">
				<InfiniteScroller
					hasMore={hasMore}
					isLoading={isLoading}
					next={loadMessages}
					threshold={1}
					reverse
				>
					{hasMore && sortedMessages.length > 0 && (
						<Loader2 className="mt-2 h-4 w-4 animate-spin" />
					)}
					{sortedMessages.map((message) => {
						const user = users.find((u) => u._id === message._user);
						const event = message.type === 'event';
						const isAuthenticatedUser =
							auth.user._id === message._user;

						const shownIndex = shownMessages.indexOf(message);
						const previous = shownMessages[shownIndex - 1];
						const next = shownMessages[shownIndex + 1];
						const startsNewDay =
							!previous ||
							!moment(previous.createdAt).isSame(message.createdAt, 'day');

						const name = user ? user.name : 'Unknown User';

						return (
							<div
								id={`${message._id}`}
								key={message._id}
								className={
									message._id === mentionTarget
										? 'w-full rounded-2xl bg-violet-50 py-1 ring-1 ring-inset ring-violet-300 dark:bg-violet-950/30 dark:ring-violet-700'
										: 'w-full'
								}
							>
								<ChatBubble
									message={message}
									_customer={activeTeamConversation?._customer}
									_business={activeTeamConversation?._business}
									isAuthenticatedUser={isAuthenticatedUser}
									event={event}
									currentTime={moment(message.createdAt).format('LT')}
									name={name}
									dateLabel={
										startsNewDay && !message.isDeleted
											? formatDayLabel(message.createdAt)
											: ''
									}
									isFirstInGroup={startsNewDay || !isSameRun(previous, message)}
									isLastInGroup={!isSameRun(message, next)}
									key={message._id}
									isDirectMessage={directMessage}
									user={user}
									reactions={message.reactions}
									_authenticatedUser={auth.user._id}
									showMessageActionsForActiveUsersInConversation={checkIfAllUsersAreActiveInCurrentConversation()}
									activeUsersInConversation={
										activeUsersInConversation
									}
									showAvatar={showAvatar}
									isRunner={isRunner}
								/>
							</div>
						);
					})}
				</InfiniteScroller>
			</div>
		</div>
	);
};

export default TeamMessages;
